// People who hold a DISTRICT seat from outside that district (District Affiliations, Phase 3).
//
// The Org Chart, the Dashboard seat count and committee health are built from roster Contacts. Someone who serves a district they
// are not rostered in has no Contact there (deliberately: no new roster record, no login, no permission), so these screens add them
// from listAffiliatedHoldersV1 instead. EVERY surface goes through the functions below, so they cannot disagree with one another.
//
// Pure (no Firebase). Nothing here is used for authorization; it only decides what is shown and which controls are offered.
import type { AffiliatedHolder, Position } from '../types';

export const SERVES_OUTSIDE_LABEL = 'Serves from outside district';
export const SERVES_OUTSIDE_TITLE = 'Registered outside this district. Registration and home district are in Contacts.';

/** The outside-district holders of one seat (a Position in one district), by name. A holder listed twice counts once. */
export function affiliatedHoldersOfSeat(holders: readonly AffiliatedHolder[] | undefined, positionId: string, districtId: string): AffiliatedHolder[] {
  const seen = new Set<string>();
  const out: AffiliatedHolder[] = [];
  for (const h of holders ?? []) {
    if (h.positionId !== positionId || h.districtId !== districtId || seen.has(h.assignmentId)) continue;
    seen.add(h.assignmentId);
    out.push(h);
  }
  return out.sort((a, b) => a.name.localeCompare(b.name) || a.assignmentId.localeCompare(b.assignmentId));
}

/** The Position ids an outside-district holder fills in each district (district id → Position ids). */
export function filledByAffiliation(holders: readonly AffiliatedHolder[] | undefined): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const h of holders ?? []) {
    const set = out.get(h.districtId) ?? new Set<string>();
    set.add(h.positionId);
    out.set(h.districtId, set);
  }
  return out;
}

/** Whether the "assign someone from outside this district" controls are offered for a seat.
 *    hidden  not an administrator, or the council has not made District Affiliations available
 *    held    a single-holder seat that already has a holder (replacing an occupied seat is not offered here)
 *    open    a vacant single-holder seat, or any multi-holder seat
 *  The server decides in the end; this only avoids offering what it would refuse. */
export type OutsideAssignState = 'hidden' | 'held' | 'open';
export function outsideAssignState(input: { admin: boolean; enabled: boolean; position: Pick<Position, 'cardinality'>; rosterHolderCount: number; affiliatedHolderCount: number }): OutsideAssignState {
  if (!input.admin || !input.enabled) return 'hidden';
  if (input.position.cardinality === 'multiple') return 'open';
  return input.rosterHolderCount + input.affiliatedHolderCount > 0 ? 'held' : 'open';
}

/** A plain-language message for a refused assignment (the server's wording is for developers). */
export function assignErrorMessage(e: unknown, fallback = 'Could not assign.'): string {
  const message = e instanceof Error ? e.message : '';
  if (/already occupied|already canonically held|already actively holds/i.test(message)) return 'This seat is already held. Replacing an occupied seat is not available here.';
  if (/not an active identity|provisional|requires review/i.test(message)) return 'That person\'s identity has to be confirmed before they can be assigned.';
  if (/administrator/i.test(message)) return 'Only an administrator can assign someone from outside the district.';
  return message || fallback;
}

/** The message for a refused ROSTER assignment (a chair adding someone from the district's roster to a seat). When the seat is held by
 *  someone serving from outside the district, the server's wording is replaced by what actually happened and what to do. */
export function rosterAssignErrorMessage(e: unknown, outsideHolderNames: readonly string[], fallback = 'Could not assign.'): string {
  const message = e instanceof Error ? e.message : '';
  if (outsideHolderNames.length > 0 && /already canonically held|already occupied|two holders/i.test(message)) {
    return `${outsideHolderNames.join(', ')} holds this seat and serves from outside the district. An administrator can remove them first.`;
  }
  return message || fallback;
}
